// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Feed: geteilte Trainings unter Götter-Alias — nie echte Namen. Online-only.
import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { pb } from "@/lib/pb"
import { loadFeed, toggleReaction, sharingEnabled } from "@/lib/feed"
import type { FeedPost, Reaction } from "@/lib/feed"
import { dayOf, fmtDate } from "@/lib/dates"
import { toast } from "@/lib/toast"
import Gluehwuermchen from "@/components/celebration/creatures/Gluehwuermchen"
import { IconArm, IconFlamme, IconKlatschen } from "@/components/icons"
import "@/styles/feed.css"

// Gespeicherte Schlüssel bleiben Emoji (PB-Daten), gezeichnet wird die Familie (§6)
const REACTIONS: Array<[Reaction["emoji"], string, (p: { size?: number }) => React.JSX.Element]> = [
  ["💪", "Stark", IconArm],
  ["🔥", "Feuer", IconFlamme],
  ["👏", "Applaus", IconKlatschen],
]

type Post = FeedPost & { reactions: Reaction[] }

export default function FeedPage() {
  const navigate = useNavigate()
  const [posts, setPosts] = useState<Post[] | null>(null)
  const uid = pb.authStore.record?.id ?? ""

  async function load() {
    try {
      setPosts(await loadFeed())
    } catch (e) {
      // PocketBase bricht bei Doppelaufrufen die erste Anfrage selbst ab —
      // das ist kein echter Fehler und verdient keinen Toast
      if ((e as { isAbort?: boolean }).isAbort) return
      toast("Der Feed braucht Internet.")
    }
  }
  useEffect(() => { void load() }, [])

  async function react(postId: string, emoji: Reaction["emoji"]) {
    try {
      await toggleReaction(postId, emoji)
      await load()
    } catch {
      toast("Der Feed braucht Internet.")
    }
  }

  if (posts === null) return null

  return (
    <>
      {!sharingEnabled() && (
        <p className="feed-hint">
          Du schaust nur zu. Teilen kannst du im <Link to="/profil">Profil</Link> aktivieren.
        </p>
      )}

      {posts.length === 0 ? (
        <div className="empty">
          <div className="empty-critter"><Gluehwuermchen /></div>
          <p><b>Noch still hier. Teile dein erstes Training.</b></p>
          <button className="btn-secondary" onClick={() => navigate("/profil")}>Zum Profil</button>
        </div>
      ) : (
        <>
          <h2>Letzte Trainings</h2>
          {/* Flache Liste mit Trennlinien (Skill §13.7) — die Überschrift ersetzt den Rahmen */}
          <div className="feed-list">
            {posts.map((p) => {
              const mine = p.reactions.find((r) => r.user === uid)?.emoji
              return (
                <article className="feed-post enter" key={p.id}>
                  <div className="fp-head">
                    <span className="fp-alias">{p.alias}</span>
                    <span className="fp-date">{fmtDate(dayOf(p.date))}</span>
                  </div>
                  {/* Label-über-Zahl statt Fließtext-Zeile (Skill §13.2) */}
                  <div className="statgrid fp-grid">
                    <div className="stat">
                      <span className="stat-lbl">Übungen</span>
                      <b className="num-hero">{p.exercises_count}</b>
                    </div>
                    <div className="stat">
                      <span className="stat-lbl">Volumen</span>
                      <b className="num-hero">{p.volume_kg.toLocaleString("de-DE")}<i>kg</i></b>
                    </div>
                    <div className="stat">
                      <span className="stat-lbl">Dauer</span>
                      <b className="num-hero">{p.duration_min}<i>min</i></b>
                    </div>
                  </div>
                  <div className="fp-reactions">
                    {REACTIONS.map(([emoji, label, Icon]) => {
                      const n = p.reactions.filter((r) => r.emoji === emoji).length
                      return (
                        <button
                          key={emoji}
                          type="button"
                          className="pressable"
                          aria-pressed={mine === emoji}
                          aria-label={`${label}: ${n}`}
                          onClick={() => void react(p.id, emoji)}
                        >
                          <Icon size={19} />{n > 0 && <span className="fp-count">{n}</span>}
                        </button>
                      )
                    })}
                  </div>
                </article>
              )
            })}
          </div>
        </>
      )}
    </>
  )
}
